import { IconButton, observeResize } from '@affine/component';
import {
  type PDF,
  type PDFMeta,
  type PDFRendererState,
  PDFService,
  PDFStatus,
} from '@affine/core/modules/pdf';
import type { PDFPage } from '@affine/core/modules/pdf/entities/pdf-page';
import type { PageSize } from '@affine/core/modules/pdf/renderer/types';
import { LoadingSvg, PDFPageCanvas } from '@affine/core/modules/pdf/views';
import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  CloseIcon,
  ExpandCloseIcon,
  ExpandFullIcon,
} from '@blocksuite/icons/rc';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { AttachmentViewerProps } from '../types';
import * as styles from './pdf-presentation.css';
import { fitToPage } from './utils';

const EMPTY_META: PDFMeta = {
  pageCount: 0,
  pageSizes: [],
  maxSize: { width: 0, height: 0 },
};

const defaultLabels = {
  close: 'Close presentation',
  previousPage: 'Previous page',
  nextPage: 'Next page',
  enterFullscreen: 'Enter fullscreen',
  exitFullscreen: 'Exit fullscreen',
  loading: 'Loading PDF',
  loadError: 'Unable to load PDF',
  pageCount: (page: number, total: number) => `${page} / ${total}`,
};

export type PDFPresentationLabels = Partial<typeof defaultLabels>;

export interface PDFPresentationHandle {
  requestFullscreen: () => Promise<void>;
  exitFullscreen: () => Promise<void>;
  isFullscreen: () => boolean;
}

export interface PDFPresentationProps extends AttachmentViewerProps {
  initialPage?: number;
  onClose: () => void;
  /**
   * Supply translated labels from the parent until this component is connected
   * to an i18n namespace.
   */
  labels?: PDFPresentationLabels;
  onFullscreenChange?: (fullscreen: boolean) => void;
  onFullscreenError?: (error: unknown) => void;
}

function clampPage(page: number, meta: PDFMeta) {
  if (!Number.isFinite(page)) return 0;
  return Math.min(
    Math.max(Math.trunc(page), 0),
    Math.max(meta.pageCount - 1, 0)
  );
}

function getPageRenderSize(
  viewport: PageSize,
  pageSize: PageSize | undefined,
  meta: PDFMeta
) {
  if (!pageSize || viewport.width <= 0 || viewport.height <= 0) {
    return null;
  }

  return fitToPage(
    {
      width: Math.max(viewport.width - 48, 1),
      height: Math.max(viewport.height - 48, 1),
    },
    pageSize,
    meta.maxSize
  );
}

function isInteractiveTarget(target: EventTarget | null) {
  return (
    target instanceof Element &&
    target.closest('button, input, textarea, select, [contenteditable]') !==
      null
  );
}

export const PDFPresentation = forwardRef<
  PDFPresentationHandle,
  PDFPresentationProps
>(function PDFPresentation(
  {
    model,
    initialPage = 0,
    onClose,
    labels: labelOverrides,
    onFullscreenChange,
    onFullscreenError,
  },
  ref
) {
  const labels: typeof defaultLabels = {
    ...defaultLabels,
    ...labelOverrides,
  };
  const rootRef = useRef<HTMLDivElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const closingRef = useRef(false);
  const pdfService = useService(PDFService);
  const blobId = useLiveData(
    useMemo(() => LiveData.fromSignal(model.props.sourceId$), [model])
  );
  const [pdf, setPdf] = useState<PDF | null>(null);
  const [pageEntity, setPageEntity] = useState<{
    page: PDFPage;
    release: () => void;
  } | null>(null);
  const [page, setPage] = useState(() => Math.max(initialPage, 0));
  const [viewport, setViewport] = useState<PageSize>({ width: 0, height: 0 });
  const [isFullscreen, setIsFullscreen] = useState(false);

  const state = useLiveData(
    useMemo(
      () =>
        pdf?.state$ ??
        new LiveData<PDFRendererState>({ status: PDFStatus.IDLE }),
      [pdf]
    )
  );
  const meta = state.status === PDFStatus.Opened ? state.meta : EMPTY_META;
  const bitmap = useLiveData(
    useMemo(() => pageEntity?.page.bitmap$ ?? null, [pageEntity])
  );
  const pageError = useLiveData(
    useMemo(() => pageEntity?.page.error$ ?? null, [pageEntity])
  );
  const renderSize = useMemo(
    () => getPageRenderSize(viewport, meta.pageSizes[page], meta),
    [meta, page, viewport]
  );

  const requestFullscreen = useCallback(async () => {
    const root = rootRef.current;
    if (!root || document.fullscreenElement === root) return;
    await root.requestFullscreen();
  }, []);

  const exitFullscreen = useCallback(async () => {
    if (document.fullscreenElement === rootRef.current) {
      await document.exitFullscreen();
    }
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      requestFullscreen,
      exitFullscreen,
      isFullscreen: () => document.fullscreenElement === rootRef.current,
    }),
    [exitFullscreen, requestFullscreen]
  );

  const close = useCallback(() => {
    if (closingRef.current) return;

    closingRef.current = true;
    void exitFullscreen()
      .catch(onFullscreenError ?? console.error)
      .finally(onClose);
  }, [exitFullscreen, onClose, onFullscreenError]);

  const goToPage = useCallback(
    (nextPage: number) => setPage(clampPage(nextPage, meta)),
    [meta]
  );

  useEffect(() => {
    setPage(clampPage(initialPage, meta));
  }, [initialPage, meta]);

  useEffect(() => {
    setPdf(null);
    setPageEntity(null);
    const canvas = canvasRef.current;
    if (canvas) {
      canvas.width = 0;
      canvas.height = 0;
    }
    if (!blobId) return;

    const entity = pdfService.get(blobId);
    setPdf(entity.pdf);
    return () => {
      entity.release();
    };
  }, [blobId, pdfService]);

  useEffect(() => {
    if (state.status !== PDFStatus.Opened || !pdf || !renderSize) return;

    const entity = pdf.page(
      page,
      `${renderSize.width}:${renderSize.height}:${window.devicePixelRatio}`
    );
    setPageEntity(entity);
    entity.page.render({
      width: renderSize.width,
      height: renderSize.height,
      scale: window.devicePixelRatio,
    });

    return () => {
      entity.page.render.unsubscribe();
      entity.release();
    };
  }, [page, pdf, renderSize, state.status]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !bitmap) return;

    const context = canvas.getContext('2d');
    if (!context) return;

    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    context.clearRect(0, 0, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0);
  }, [bitmap]);

  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    let frame = 0;
    const dispose = observeResize(
      stage,
      ({ contentRect: { width, height } }) => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          setViewport(current =>
            current.width === width && current.height === height
              ? current
              : { width, height }
          );
        });
      }
    );

    return () => {
      dispose();
      cancelAnimationFrame(frame);
    };
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const active = document.fullscreenElement === rootRef.current;
      setIsFullscreen(active);
      onFullscreenChange?.(active);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    handleFullscreenChange();
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, [onFullscreenChange]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (
        document.fullscreenElement !== rootRef.current &&
        !rootRef.current?.contains(document.activeElement)
      ) {
        return;
      }

      if (event.key === 'Escape') {
        close();
        event.preventDefault();
        event.stopPropagation();
        return;
      }

      if (isInteractiveTarget(event.target)) return;

      switch (event.key) {
        case 'ArrowLeft':
        case 'PageUp':
          goToPage(page - 1);
          break;
        case 'ArrowRight':
        case 'PageDown':
        case ' ':
        case 'Spacebar':
          goToPage(page + 1);
          break;
        case 'Home':
          goToPage(0);
          break;
        case 'End':
          goToPage(meta.pageCount - 1);
          break;
        default:
          return;
      }

      event.preventDefault();
      event.stopPropagation();
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [close, goToPage, meta.pageCount, page]);

  useEffect(() => {
    return () => {
      if (
        !closingRef.current &&
        document.fullscreenElement === rootRef.current
      ) {
        void document.exitFullscreen().catch(console.error);
      }
    };
  }, []);

  const hasPreviousPage = page > 0;
  const hasNextPage = page < meta.pageCount - 1;
  const loading =
    state.status === PDFStatus.IDLE || state.status === PDFStatus.Opening;

  return (
    <div ref={rootRef} className={styles.root} data-testid="pdf-presentation">
      <main ref={stageRef} className={styles.stage}>
        {state.status === PDFStatus.Error || pageError ? (
          <div className={styles.status} role="alert">
            {labels.loadError}
          </div>
        ) : (
          <div className={styles.page}>
            <PDFPageCanvas
              ref={canvasRef}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
              }}
            />
          </div>
        )}
        {loading ||
        (state.status === PDFStatus.Opened && !bitmap && !pageError) ? (
          <div
            className={styles.loading}
            role="status"
            aria-label={labels.loading}
          >
            <LoadingSvg className={styles.loadingIndicator} />
          </div>
        ) : null}
      </main>

      <div className={styles.toolbar}>
        <div className={styles.controls}>
          <IconButton
            data-testid="pdf-presentation-previous"
            tooltip={labels.previousPage}
            aria-label={labels.previousPage}
            icon={<ArrowLeftSmallIcon />}
            className={styles.controlButton}
            disabled={!hasPreviousPage}
            onClick={() => goToPage(page - 1)}
          />
          <output className={styles.pageIndicator} aria-live="polite">
            {meta.pageCount > 0
              ? labels.pageCount(page + 1, meta.pageCount)
              : '-'}
          </output>
          <IconButton
            data-testid="pdf-presentation-next"
            tooltip={labels.nextPage}
            aria-label={labels.nextPage}
            icon={<ArrowRightSmallIcon />}
            className={styles.controlButton}
            disabled={!hasNextPage}
            onClick={() => goToPage(page + 1)}
          />
        </div>
        <div className={styles.controls}>
          <IconButton
            data-testid="pdf-presentation-fullscreen"
            tooltip={
              isFullscreen ? labels.exitFullscreen : labels.enterFullscreen
            }
            aria-label={
              isFullscreen ? labels.exitFullscreen : labels.enterFullscreen
            }
            icon={isFullscreen ? <ExpandCloseIcon /> : <ExpandFullIcon />}
            className={styles.controlButton}
            onClick={() => {
              const action = isFullscreen
                ? exitFullscreen()
                : requestFullscreen();
              void action.catch(onFullscreenError ?? console.error);
            }}
          />
          <IconButton
            data-testid="pdf-presentation-close"
            data-attachment-presentation-close
            tooltip={labels.close}
            aria-label={labels.close}
            icon={<CloseIcon />}
            className={styles.controlButton}
            onClick={close}
          />
        </div>
      </div>
    </div>
  );
});

PDFPresentation.displayName = 'PDFPresentation';
